import { motion } from 'motion/react';
import { type ReactNode, useEffect, useState } from 'react';

export type PageTransitionProps = {
  /** Stable per-route key. */
  routeKey: string;
  children: ReactNode;
};

/** Halftone diagonal wipe on route change. Respects `prefers-reduced-motion`. */
export const PageTransition = ({ routeKey, children }: PageTransitionProps) => {
  const reduced = usePrefersReducedMotion();
  if (reduced) return <div className="h-full w-full">{children}</div>;

  return (
    <div key={routeKey} className="relative h-full w-full">
      <div className="h-full w-full">{children}</div>
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[100]"
        style={{
          background: 'var(--color-curtain)',
          maskImage:
            'linear-gradient(225deg, #000 calc(50% - 0.5px), transparent calc(50% + 0.5px))',
          WebkitMaskImage:
            'linear-gradient(225deg, #000 calc(50% - 0.5px), transparent calc(50% + 0.5px))',
          maskSize: '280% 280%',
          WebkitMaskSize: '280% 280%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
        }}
        initial={{ maskPosition: '100% 0%' }}
        animate={{ maskPosition: '0% 100%' }}
        transition={{ duration: 0.8, ease: [0.5, 0.05, 0.5, 0.95], times: [0, 0.25, 1] }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[100]"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--color-curtain) 50%, transparent 51%)',
          backgroundSize: '11px 11px',
          maskImage: 'linear-gradient(225deg, transparent 42%, #000 50%, transparent 58%)',
          WebkitMaskImage: 'linear-gradient(225deg, transparent 42%, #000 50%, transparent 58%)',
          maskSize: '280% 280%',
          WebkitMaskSize: '280% 280%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
        }}
        initial={{ maskPosition: '100% 0%', opacity: 0 }}
        animate={{
          maskPosition: '0% 100%',
          opacity: [0, 0, 1, 1, 0],
        }}
        transition={{
          duration: 0.8,
          ease: [0.5, 0.05, 0.5, 0.95],
          times: [0, 0.22, 0.28, 0.92, 1],
        }}
      />
    </div>
  );
};

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};
